import { workloadConfig } from "app-config";
import { AxiosRequestConfig } from "axios";

/**
 * The only place allowed to build an `Authorization: Bearer` header (see the `no-restricted-syntax` rule in
 * `.eslintrc`). A token is attached only when the request host ends with one of
 * `workloadConfig.authAllowedDomains`, declared per environment on the workload. A missing key means no
 * request carries a token (fail closed).
 */

const AUTHORIZATION = "Authorization";

const toBearer = (token: string) => `Bearer ${token}`;

const normalizeDomain = (value: unknown) => {
  if (typeof value !== "string") return null;
  const domain = value
    .trim()
    .toLowerCase()
    .replace(/^\*?\./, "");
  // A suffix is a bare host name: no scheme, port, path or wildcard, and at least one dot
  if (!domain.includes(".") || /[/:*\s]/.test(domain)) return null;
  return domain;
};

// Same `error` event as `trackError` in utils/request. `window.track` only exists once `ReactTracker` is built in
// App.tsx, which runs after this module loads, so the event is sent on the next tick
const reportConfigError = (errorCode: string, errorMessage: string) => {
  console.error(`[auth-header] ${errorMessage}`);
  setTimeout(() => {
    if (typeof window.track !== "function") return;
    window.track("error", { errorSource: "erp_staff_desktop", errorCode, errorMessage });
  });
};

const parseAllowedDomains = (values: unknown) => {
  if (!Array.isArray(values)) {
    reportConfigError(
      "AUTH_ALLOWED_DOMAINS_MISSING",
      "workloadConfig.authAllowedDomains is missing, no request will carry a token"
    );
    return [];
  }
  const domains = values.map(normalizeDomain).filter((domain): domain is string => !!domain);
  const invalid = values.filter((value) => !normalizeDomain(value));
  if (invalid.length) {
    reportConfigError(
      "AUTH_ALLOWED_DOMAINS_INVALID",
      `ignored invalid authAllowedDomains entries: ${invalid.map((value) => JSON.stringify(value)).join(", ")}`
    );
  }
  return domains;
};

const allowedDomains = parseAllowedDomains(workloadConfig?.authAllowedDomains);

// Same rules as axios `isAbsoluteURL` + `combineURLs`, so the host checked here is the host the request goes to
const isAbsoluteURL = (url: string) => /^([a-z][a-z\d+\-.]*:)?\/\//i.test(url);
const combineURLs = (baseURL: string, relativeURL: string) =>
  relativeURL ? `${baseURL.replace(/\/+$/, "")}/${relativeURL.replace(/^\/+/, "")}` : baseURL;

const resolveUrl = (url = "", baseURL?: string) => {
  const fullPath = baseURL && !isAbsoluteURL(url) ? combineURLs(baseURL, url) : url;
  try {
    return new URL(fullPath, window.location.href);
  } catch {
    return null;
  }
};

const isTokenAllowed = (url?: string, baseURL?: string, domains: string[] = allowedDomains) => {
  const target = resolveUrl(url, baseURL);
  if (!target || target.protocol !== "https:" || target.port !== "") return false;
  const hostname = target.hostname.toLowerCase();
  return domains.some((domain) => hostname === domain || hostname.endsWith(`.${domain}`));
};

/** Headers for a request made outside the axios clients (`fetch`, plain `axios`, upload components). */
const getAuthorizationHeader = (
  url: string,
  token?: string,
  domains: string[] = allowedDomains
): Record<string, string> =>
  token && isTokenAllowed(url, undefined, domains) ? { [AUTHORIZATION]: toBearer(token) } : {};

/** Request interceptor body: removes any `Authorization` set elsewhere, then adds it back only for an allowed host. */
const applyAuthorization = (config: AxiosRequestConfig, token?: string, domains: string[] = allowedDomains) => {
  const headers = config.headers || {};
  [headers, headers.common, config.method && headers[config.method]].forEach((group) => {
    if (group) delete group[AUTHORIZATION];
  });
  if (token && isTokenAllowed(config.url, config.baseURL, domains)) headers[AUTHORIZATION] = toBearer(token);
  config.headers = headers;
  return config;
};

export default {
  toBearer,
  normalizeDomain,
  parseAllowedDomains,
  isTokenAllowed,
  getAuthorizationHeader,
  applyAuthorization,
};
